import mixpanel from 'mixpanel-browser';
import {
  URL_DERIVED_PROPERTY_BLACKLIST,
  sanitizeInitialReferrer,
  scrubUrlsBeforeSend,
  scrubUrlsBeforeTrack,
} from './scrubUrls';

/**
 * Super properties that say which tenant the events come from. The selected
 * restaurant is per tab (redux), but mixpanel's persisted super properties live in
 * localStorage, shared by every tab of the origin, and are re-read on every track —
 * so a restaurant registered there by one tab rides on the next event of every other
 * tab. These keys are kept in memory instead, per tab, and merged into each event;
 * none of them is ever left in mixpanel's storage.
 *
 * The WLP Mixpanel V1 keys (see analyticsContext.js), and the three the dashboard
 * registered before them.
 */
export const TAB_CONTEXT_PROPERTIES = [
  'partner_id',
  'restaurant_id',
  'restaurant_source',
  'actor_role',
  'is_test',
  'company_id',
  'franchise_id',
  'service_provider_id',
];

const isTabContextProperty = (key) => TAB_CONTEXT_PROPERTIES.includes(key);

/**
 * For an event tracked while the page is going away (tab closed, reload, a hard
 * navigation): sent at once by navigator.sendBeacon. Without them mixpanel queues
 * the event for its next batch, which a page that is being unloaded may never
 * send — its own unload flush can run before the event is queued.
 */
export const UNLOAD_TRACK_OPTIONS = { transport: 'sendBeacon', send_immediately: true };

class Analytics {
  constructor() {
    this.initialized = false;
    // The group each key was last set to, so a context that re-applies the same
    // restaurant does not send the profile update `set_group` makes every time.
    this.groups = {};
    // This tab's values of TAB_CONTEXT_PROPERTIES.
    this.context = {};
  }

  init(token) {
    if (this.initialized || !token) {
      return;
    }

    mixpanel.init(token, {
      api_host: 'https://api-eu.mixpanel.com',
      autocapture: false,
      record_sessions_percent: 0,
      debug: import.meta.env.VITE_APP_MODE !== 'production',
      persistence: 'localStorage',
      // The platform project's rule, as in shop-web: UTM values ride on the events of
      // the page load that carries them, and are not persisted onto every later one.
      stop_utm_persistence: true,
      // No secret or personal data in any event. The URLs the SDK attaches on its
      // own can carry a magic-link or password-reset token, a Stripe client secret or
      // a guest's name, so their query is allowlisted (see scrubUrls.js) before the
      // event is queued, and once more before it is sent.
      hooks: { before_track: scrubUrlsBeforeTrack(mixpanel), before_send_events: scrubUrlsBeforeSend },
      property_blacklist: URL_DERIVED_PROPERTY_BLACKLIST,
      // The SDK sends the first-touch referrer to the people profile right after
      // this callback: scrub it first.
      loaded: (mp) => sanitizeInitialReferrer(mp),
    });

    mixpanel.register({
      app: 'restaurant-dashboard',
      env: import.meta.env.VITE_APP_MODE,
    });

    this.initialized = true;
    // Earlier builds persisted these; nothing may inherit them now.
    this.dropPersistedTabContext();
  }

  identify(userId, traits = {}) {
    if (!this.isReady()) {
      return;
    }

    mixpanel.identify(String(userId));

    if (Object.keys(traits).length) {
      mixpanel.people.set(traits);
    }
  }

  /** Remove properties from the current user's profile. */
  unsetProfileProperties(propertyNames) {
    if (!this.isReady() || !propertyNames?.length) {
      return;
    }

    mixpanel.people.unset(propertyNames);
  }

  /**
   * Register super properties. The tenant keys (TAB_CONTEXT_PROPERTIES) are kept
   * for this tab only; everything else is persisted by mixpanel as usual.
   */
  register(properties) {
    if (!this.isReady() || !properties) {
      return;
    }

    const persisted = {};

    Object.entries(properties).forEach(([key, value]) => {
      if (isTabContextProperty(key)) {
        this.context[key] = value;
      } else {
        persisted[key] = value;
      }
    });

    if (Object.keys(persisted).length) {
      mixpanel.register(persisted);
    }
  }

  unregister(propertyName) {
    if (!this.isReady() || !propertyName) {
      return;
    }

    delete this.context[propertyName];
    mixpanel.unregister(propertyName);
  }

  /**
   * Put the user and every following event of this tab in a Mixpanel group
   * (`partner_id`, `restaurant_id`). `set_group` associates the profile with the
   * group, and also persists the key — as a one-element list, for every tab — so
   * that copy is removed again: this tab's events carry the bare integer the
   * backend sends, from memory, and the groups join across both.
   */
  setGroup(groupKey, groupId) {
    if (!this.isReady() || !groupKey || groupId === null || groupId === undefined) {
      return;
    }

    this.context[groupKey] = groupId;

    if (this.groups[groupKey] === groupId) {
      return;
    }

    mixpanel.set_group(groupKey, groupId);
    mixpanel.unregister(groupKey);
    this.groups[groupKey] = groupId;
  }

  /**
   * Stop attributing this tab's events to a group, when its context is left. The
   * profile keeps its last group; the next `setGroup` replaces it.
   */
  unsetGroup(groupKey) {
    if (!this.isReady() || !groupKey) {
      return;
    }

    delete this.context[groupKey];
    mixpanel.unregister(groupKey);
    delete this.groups[groupKey];
  }

  /**
   * Track an event with this tab's tenant context; a property passed here wins
   * over it (an event about a restaurant that is not the selected one yet).
   *
   * `options` goes to mixpanel as it is — UNLOAD_TRACK_OPTIONS for an event sent
   * while the page is going away.
   */
  track(event, properties = {}, options = undefined) {
    if (!this.isReady()) {
      return;
    }

    // Mixpanel merges what is in storage into the event, under the properties
    // passed here: a tenant key this tab does not hold must not be read from
    // there, where another tab (or an older build) may have left its own.
    this.dropPersistedTabContext();

    const payload = { ...this.context, ...properties };

    if (options) {
      mixpanel.track(event, payload, options);
    } else {
      mixpanel.track(event, payload);
    }
  }

  reset() {
    if (!this.isReady()) {
      return;
    }

    mixpanel.reset();
    this.groups = {};
    this.context = {};
    mixpanel.register({
      app: 'restaurant-dashboard',
      env: import.meta.env.VITE_APP_MODE,
    });
  }

  isReady() {
    return this.initialized && mixpanel?.__loaded === true;
  }

  /** Remove the tenant keys from mixpanel's storage (a no-op for a key that is not there). */
  dropPersistedTabContext() {
    TAB_CONTEXT_PROPERTIES.forEach((key) => mixpanel.unregister(key));
  }
}

export default new Analytics();
